import { Outlet, useLocation, Navigate } from "react-router-dom"
import { Sidebar, SIDEBAR_WIDTH_MINI, SIDEBAR_WIDTH_EXPANDED } from "./Sidebar"
import { Header } from "./Header"
import { FloatingActionButton } from "./FloatingActionButton"
import { CommandPalette } from "@/components/ui"
import { useState, useEffect } from "react"
import { useAuthStore } from "@/stores"

/** Rutas que el Admin puede ver: Inicio, Usuarios (crear A), Inventario de SIMs (asignar a A por familia), Perfil. */
function isAdminAllowedPath(pathname: string): boolean {
  if (pathname === "/index" || pathname === "/dashboard") return true
  if (pathname.startsWith("/usuarios")) return true
  if (pathname === "/inventario-de-sims" || pathname === "/sims-personales") return true
  if (pathname === "/perfil") return true
  return false
}

export function MainLayout() {
  const location = useLocation()
  const { user } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [commandOpen, setCommandOpen] = useState(false)
  const [miniState, setMiniState] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  // Admin solo puede acceder a Inicio, Usuarios, Inventario de SIMs, Perfil
  if (user?.tipo_usuario === "Admin" && !isAdminAllowedPath(location.pathname)) {
    return <Navigate to="/index" replace />
  }

  // Atajo ⌘K / Ctrl+K para command palette
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        setCommandOpen((open) => !open)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener("resize", checkMobile)
    
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Calcular el margen izquierdo basado en el estado del sidebar
  const getMarginLeft = () => {
    if (isMobile) {
      return 0 // En móvil el sidebar es overlay, no hay margen
    }
    return miniState ? SIDEBAR_WIDTH_MINI : SIDEBAR_WIDTH_EXPANDED
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        miniState={miniState}
        onMiniStateChange={setMiniState}
      />

      {/* Main content */}
      <div
        className="transition-all duration-300 min-h-screen flex flex-col"
        style={{ marginLeft: getMarginLeft() }}
      >
        <Header onMenuClick={() => setSidebarOpen(true)} onCommandClick={() => setCommandOpen(true)} />
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      <FloatingActionButton />
    </div>
  )
}
